import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  AspectRatio,
  Box,
  Button,
  Center,
  Heading,
  Link,
  Spinner,
  Text,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { LinkIcon } from 'components/icons';
import React, { useRef } from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { BridgeViewer_node$key } from './__generated__/BridgeViewer_node.graphql';
import { isGoogleDriveUrl } from 'utils/isGoogleDriveUrl';
import { useFeatureFlag } from '../../../../../utils/sessionProvider';
import { isOneDriveUrl } from 'utils/isOneDriveUrl';
import { isDropBoxUrl } from 'utils/isDropBoxUrl';
import { useInjectScript } from 'hooks/useInjectScript';
import { useCardboardEnv } from 'utils/useCardboardEnv';

const fileNodeFragment = graphql`
  fragment BridgeViewer_node on Node {
    id
    ... on FolderItem {
      id
      name
      content {
        __typename
        ... on BridgeInterface {
          url
        }
      }
    }
  }
`;

interface BridgeViewerProps {
  node: BridgeViewer_node$key;
}

export const BridgeViewer = ({ node }: BridgeViewerProps) => {
  const { name, content } = useFragment(fileNodeFragment, node);

  const isFeatureGoogleDriveIntegrationEnabled = useFeatureFlag(
    'FEATURE_GOOGLE_DRIVE_INTEGRATION_ITEM_VIEWER',
  );

  const isFeatureOneDriveIntegrationEnabled = useFeatureFlag(
    'FEATURE_ONE_DRIVE_INTEGRATION_ITEM_VIEWER',
  );
  const isFeatureDropBoxIntegrationEnabled = useFeatureFlag(
    'FEATURE_DROPBOX_INTEGRATION_ITEM_VIEWER',
  );

  if (!content?.__typename?.includes('Bridge')) {
    return null;
  }

  if (
    isFeatureGoogleDriveIntegrationEnabled &&
    content.url &&
    isGoogleDriveUrl(content.url)
  ) {
    return <GoogleDriveViewer url={content.url} />;
  }

  if (
    isFeatureOneDriveIntegrationEnabled &&
    content.url &&
    isOneDriveUrl(content.url)
  ) {
    return <OneDriveViewer url={content.url} />;
  }

  if (
    isFeatureDropBoxIntegrationEnabled &&
    content.url &&
    isDropBoxUrl(content.url)
  ) {
    return <DropboxViewer url={content.url} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
      }}
    >
      <VStack
        width="100%"
        maxW={480}
        height={200}
        p={6}
        bg="blue.500"
        color="gray.50"
        borderRadius="md"
        display="flex"
        justifyContent="center"
        alignItems="center"
        spacing={3}
      >
        <Heading size="lg">{t`Bridge`}</Heading>

        <Text>{t`A bridge is a link to a resource outside of Govity.`}</Text>
        <Button
          color="gray.700"
          as={Link}
          toExternal={content.url}
          leftIcon={<LinkIcon />}
        >
          {' '}
          {name}
        </Button>
      </VStack>
    </div>
  );
};

const SimpleIframeViewer = ({ url }: { url: string }) => (
  <AspectRatio w="100%">
    <iframe src={url} />
  </AspectRatio>
);

const GoogleDriveViewer = SimpleIframeViewer;
const OneDriveViewer = SimpleIframeViewer;

type DropboxEmbedId = symbol;

declare global {
  interface Window {
    Dropbox?: {
      embed: (
        options: {
          link: string;
          file?: { zoom: 'best' | 'fit' };
          folder?: {
            view: 'grid' | 'list';
            headerSize: 'normal' | 'small';
          };
        },
        mountPoint: HTMLDivElement,
      ) => DropboxEmbedId;
      unmount: (id: DropboxEmbedId) => void;
    };
  }
}

const DropboxViewer = ({ url }: { url: string }) => {
  const { DROPBOX_APP_KEY } = useCardboardEnv();

  if (!DROPBOX_APP_KEY) {
    return <ErrorDropboxViewer url={url} missingAppKey />;
  } else {
    return <InnerDropboxViewer url={url} appKey={DROPBOX_APP_KEY} />;
  }
};

const InnerDropboxViewer = ({
  url,
  appKey,
}: {
  url: string;
  appKey: string;
}) => {
  const [loadedScript, scriptError] = useInjectScript(
    'https://www.dropbox.com/static/api/2/dropins.js',
    {
      'data-app-key': appKey,
      id: 'dropboxjs',
    },
  );

  const ref = useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (!loadedScript || scriptError || !ref.current || !window.Dropbox) {
      return;
    }

    const embed = window.Dropbox.embed(
      {
        link: url,
        file: {
          zoom: 'best',
        },
        folder: {
          view: 'grid',
          headerSize: 'normal',
        },
      },
      ref.current,
    );
    return () => {
      if (window.Dropbox) {
        window.Dropbox.unmount(embed);
      }
    };
  }, [loadedScript, ref.current, scriptError]);

  if (!loadedScript) {
    return (
      <AspectRatio w="100%">
        <VStack>
          <Spinner />
          <Text textAlign="center">
            {t`Loading Dropbox viewer`}
            <br />
            <a href={url}>{t`Open in dropbox.`}</a>
          </Text>
        </VStack>
      </AspectRatio>
    );
  }

  if (scriptError) {
    return <ErrorDropboxViewer url={url} />;
  }

  return (
    <AspectRatio w="100%">
      <div ref={ref} />
    </AspectRatio>
  );
};

const ErrorDropboxViewer = ({
  url,
  missingAppKey,
}: {
  url: string;
  missingAppKey?: boolean;
}) => (
  <AspectRatio w="100%">
    <Center>
      <Alert status="error" maxWidth={350}>
        <AlertIcon />
        <Box>
          <AlertTitle>{t`Could not load Dropbox viewer`}</AlertTitle>
          <AlertDescription>
            <a href={url}>{t`Open the link in dropbox`}</a>
            <Box hidden>
              {missingAppKey
                ? 'Missing app key'
                : 'Dropbox script failed to load'}
            </Box>
          </AlertDescription>
        </Box>
      </Alert>
    </Center>
  </AspectRatio>
);
